import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiDownload, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type {
  EmployeeImportPreviewResponse,
  EmployeeImportResultResponse,
} from "@/types/generated/cms.generated";
import { errorMessage } from "@/lib/errorMessage";

/**
 * Employee CSV import, run by the Athlyn team ON BEHALF of a client organization.
 * CMS API (multipart):
 *  - `GET  /organizations/{id}/employees/import/template` → the CSV template
 *  - `POST /organizations/{id}/employees/import/preview`  (file) → per-row new/update/unchanged/error + seat impact
 *  - `POST /organizations/{id}/employees/import/commit`   (file + createMissingOrgUnits) → atomic import result
 * The server refuses a commit while the file has errors (`IMPORT_HAS_ERRORS`).
 */

const TEMPLATE_FILENAME = "employee-import-template.csv";

const employeesKey = (organizationId: string | null | undefined) =>
  ["/organizations", organizationId, "employees"] as const;

function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export function useDownloadEmployeeImportTemplate(organizationId: string | null | undefined) {
  const { toast } = useToast();
  return useMutation({
    retry: false,
    mutationFn: async () => {
      const { blob, filename } = await apiDownload(
        `/organizations/${organizationId}/employees/import/template`,
        TEMPLATE_FILENAME,
      );
      saveBlob(blob, filename);
    },
    onError: (error: unknown) => {
      toast({
        title: "Error",
        description: errorMessage(error, "Failed to download the template. Please try again."),
        variant: "destructive",
      });
    },
  });
}

export function usePreviewEmployeeImport(organizationId: string | null | undefined) {
  return useMutation({
    retry: false,
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append("file", file);
      return apiRequest<EmployeeImportPreviewResponse>(
        "POST",
        `/organizations/${organizationId}/employees/import/preview`,
        form,
      );
    },
  });
}

export interface CommitEmployeeImportInput {
  file: File;
  createMissingOrgUnits: boolean;
}

export function useCommitEmployeeImport(organizationId: string | null | undefined) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    // Never auto-retry the commit — it creates records.
    retry: false,
    mutationFn: ({ file, createMissingOrgUnits }: CommitEmployeeImportInput) => {
      const form = new FormData();
      form.append("file", file);
      form.append("createMissingOrgUnits", createMissingOrgUnits ? "true" : "false");
      return apiRequest<EmployeeImportResultResponse>(
        "POST",
        `/organizations/${organizationId}/employees/import/commit`,
        form,
      );
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: employeesKey(organizationId) });
      toast({
        title: "Employees imported",
        description: `${result?.created ?? 0} created, ${result?.updated ?? 0} updated.`,
      });
    },
  });
}

const IMPORT_ERROR_MESSAGES: Record<string, string> = {
  IMPORT_HAS_ERRORS: "The file still has errors. Fix them, then preview it again.",
  DOMAINS_BLOCKED_FOR_ENTRA:
    "This organization signs in with Microsoft Entra — its employees are synced from Entra, not imported.",
};

/**
 * Inline message for a failed preview/commit: a known typed code gets its curated text; otherwise
 * the API's `error.details` (the actionable part, e.g. "…near line 5: check for a stray or
 * unbalanced double quote") wins over the generic `error.message` ("Validation failed").
 */
export function employeeImportErrorMessage(error: unknown): string {
  const err = error as
    | { errorCode?: string; message?: string; errorDetails?: unknown }
    | null
    | undefined;
  if (err?.errorCode && IMPORT_ERROR_MESSAGES[err.errorCode]) {
    return IMPORT_ERROR_MESSAGES[err.errorCode];
  }
  if (typeof err?.errorDetails === "string" && err.errorDetails.trim()) {
    return err.errorDetails.trim();
  }
  return err?.message || "The import failed. Please try again.";
}
